import { useCallback, useEffect, useState } from 'react';
import { Navigate, useLocation, useNavigate } from 'react-router-dom';
import { useSessionToken, getSessionToken, clearSessionToken } from './session';
import TrialConversionGate from '../components/TrialConversionGate';
import TrialExpiryBanner from '../components/recovery/TrialExpiryBanner';
import MobileDesktopBanner from '../components/MobileDesktopBanner';
import { PipelineProvider } from '../context/PipelineContext';

// Authentication and entitlement are distinct: billing/support remain reachable
// after expiry, while report access continues to require the server's policy.
export const ProtectedRoute = ({ children, allowSupport = false, api }) => {
  const API = api;
  const location = useLocation();
  const navigate = useNavigate();
  const token = useSessionToken();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);
  const [confirmedToken, setConfirmedToken] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [statusError, setStatusError] = useState(false);
  const [retry, setRetry] = useState(0);

  const handleLogout = useCallback(async () => {
    const closingToken = getSessionToken();
    // Clear local ownership immediately, including the assistant/session observers.
    clearSessionToken();
    navigate('/', { replace: true });
    if (closingToken) {
      try { await fetch(`${API}/auth/logout`, { method: 'POST', headers: { Authorization: `Bearer ${closingToken}` } }); }
      catch (_) { /* Local session remains closed; no account-switch resurrection. */ }
    }
  }, [navigate, API]);

  useEffect(() => {
    const controller = new AbortController();
    let statusBusy = false;
    let lastStatusAt = 0;
    let expiryTimer;
    setIsAuthenticated(null); setUser(null); setConfirmedToken(null);
    setSubscription(null); setStatusError(false);
    const ownsSession = () => !controller.signal.aborted && getSessionToken() === token;
    const armExpiry = (data) => {
      clearTimeout(expiryTimer);
      const expires = Date.parse(data?.trial_expires_at);
      if (!data?.is_trial || !Number.isFinite(expires)) return;
      const delay = expires - Date.now() + 250;
      // Re-arm long deadlines instead of overflowing the browser timer.
      expiryTimer = setTimeout(() => { if (ownsSession()) refreshStatus(); }, Math.max(1000, Math.min(delay, 2147483647)));
    };
    const acceptStatus = async (response) => {
      if (!response?.ok) throw new Error('Subscription status unavailable');
      const data = await response.json();
      if (!ownsSession()) return;
      setSubscription(data); setStatusError(false); armExpiry(data);
    };
    const refreshStatus = async () => {
      if (!token || !ownsSession() || statusBusy) return;
      statusBusy = true; lastStatusAt = Date.now();
      try {
        await acceptStatus(await fetch(`${API}/subscriptions/status`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }));
      } catch (_) { if (ownsSession()) setStatusError(true); }
      finally { statusBusy = false; }
    };
    const checkAuth = async () => {
      if (!token) { setIsAuthenticated(false); return; }
      try {
        const [authRes, subRes] = await Promise.all([
          fetch(`${API}/auth/me`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }),
          fetch(`${API}/subscriptions/status`, { headers: { Authorization: `Bearer ${token}` }, signal: controller.signal }).catch(() => null),
        ]);
        if (!ownsSession()) return;
        if (authRes.status === 401) clearSessionToken();
        if (!authRes.ok) throw new Error('Not authenticated');
        const userData = await authRes.json();
        if (!ownsSession()) return;
        setIsAuthenticated(true); setUser(userData); setConfirmedToken(token);
        lastStatusAt = Date.now();
        try { await acceptStatus(subRes); }
        catch (_) { if (ownsSession()) setStatusError(true); }
      } catch (_) { if (ownsSession()) setIsAuthenticated(false); }
    };
    const onResume = () => {
      if (document.visibilityState !== 'hidden' && Date.now() - lastStatusAt > 30000) refreshStatus();
    };
    const onEntitlementChanged = () => refreshStatus();
    checkAuth();
    window.addEventListener('focus', onResume);
    document.addEventListener('visibilitychange', onResume);
    window.addEventListener('bds-entitlement-changed', onEntitlementChanged);
    return () => {
      controller.abort(); clearTimeout(expiryTimer);
      window.removeEventListener('focus', onResume);
      document.removeEventListener('visibilitychange', onResume);
      window.removeEventListener('bds-entitlement-changed', onEntitlementChanged);
    };
  }, [token, retry, API]);

  if (isAuthenticated === null || (isAuthenticated && confirmedToken !== token) || (!allowSupport && !subscription && !statusError && isAuthenticated)) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center"><span className="font-mono text-xs uppercase tracking-widest text-slate-500">Verifying session…</span></div>;
  }
  if (!isAuthenticated || !token) return <Navigate to="/" replace />;
  if (statusError && !allowSupport) {
    return <div className="min-h-screen bg-gray-50 p-6 flex items-center justify-center"><section className="w-full max-w-md bg-white border border-slate-200 rounded-sm p-5" role="alert">
      <h1 className="text-lg font-semibold text-slate-900">Access status is unavailable</h1>
      <p className="text-sm text-slate-600 mt-2">Your session is signed in. Check access again or contact the team.</p>
      <div className="flex flex-wrap gap-3 mt-4">
        <button className="bg-slate-900 text-white rounded-sm px-3 py-2 text-sm" onClick={() => setRetry(value => value + 1)}>Check again</button>
        <button className="text-emerald-700 text-sm" onClick={() => navigate('/support')}>Open help</button>
        <button className="text-slate-600 text-sm" onClick={handleLogout}>Sign out</button>
      </div>
    </section></div>;
  }
  if (subscription?.is_expired_trialist && !allowSupport) {
    return <TrialConversionGate user={user} returnTo={`${location.pathname}${location.search}`} onLogout={handleLogout} />;
  }
  return <>
    <MobileDesktopBanner />
    {!allowSupport && !location.pathname.startsWith('/admin') && <TrialExpiryBanner subscription={subscription} returnTo={`${location.pathname}${location.search}`} />}
    <PipelineProvider key={confirmedToken} sessionToken={confirmedToken} userId={user.user_id}>
      {children({ user, setUser, handleLogout })}
    </PipelineProvider>
  </>;
};

export default ProtectedRoute;
